import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import PageHeader from "@/components/feature/PageHeader";
import FilterGroup from "@/components/base/FilterGroup";
import WorkCard from "@/components/base/WorkCard";
import Pagination from "@/components/base/Pagination";
import EmptyState from "@/components/base/EmptyState";
import { getAllWorks } from "@/lib/content";
import useDocumentTitle from "@/hooks/useDocumentTitle";

const PAGE_SIZE = 8;

export default function WorksCategoryPage() {
  const allWorks = getAllWorks();
  const [searchParams] = useSearchParams();

  const genreOptions = useMemo(() => {
    const set = new Set<string>();
    allWorks.forEach((w) => w.genre.forEach((g) => set.add(g)));
    return [{ label: "全部", value: "all" }, ...Array.from(set).map((g) => ({ label: g, value: g }))];
  }, [allWorks]);

  const yearOptions = useMemo(() => {
    const set = new Set<number>();
    allWorks.forEach((w) => set.add(w.year));
    const sorted = Array.from(set).sort((a, b) => b - a);
    return [{ label: "全部", value: "all" }, ...sorted.map((y) => ({ label: `${y}`, value: `${y}` }))];
  }, [allWorks]);

  const regionOptions = useMemo(() => {
    const set = new Set<string>();
    allWorks.forEach((w) => set.add(w.region));
    return [{ label: "全部", value: "all" }, ...Array.from(set).map((r) => ({ label: r, value: r }))];
  }, [allWorks]);

  const sortOptions = [
    { label: "热度（评分）", value: "rating" },
    { label: "最新（年份）", value: "year" },
  ];

  const [genre, setGenre] = useState("all");
  const [year, setYear] = useState("all");
  const [region, setRegion] = useState("all");
  const [sort, setSort] = useState("rating");
  const [page, setPage] = useState(1);

  useEffect(() => {
    const g = searchParams.get("genre");
    if (g) {
      setGenre(g);
      setPage(1);
    }
  }, [searchParams]);

  const filtered = useMemo(() => {
    let list = allWorks.filter((w) => {
      if (genre !== "all" && !w.genre.includes(genre)) return false;
      if (year !== "all" && `${w.year}` !== year) return false;
      if (region !== "all" && w.region !== region) return false;
      return true;
    });
    list = [...list].sort((a, b) =>
      sort === "rating" ? b.rating - a.rating : b.year - a.year,
    );
    return list;
  }, [allWorks, genre, year, region, sort]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const currentPage = Math.min(page, totalPages);
  const pageItems = filtered.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);

  const update = (setter: (v: string) => void) => (v: string) => {
    setter(v);
    setPage(1);
  };

  const reset = () => {
    setGenre("all");
    setYear("all");
    setRegion("all");
    setSort("rating");
    setPage(1);
  };

  useDocumentTitle(
    "影视作品库 - 黑皮辣妹",
    "黑皮辣妹影视作品库，按类型、年代、地区检索影视作品，查看评分、主创与剧情简介。",
  );

  return (
    <div className="hp-fade-up">
      <PageHeader
        title="影视作品库"
        description="按类型、年代、地区多维度检索影视作品，查看评分、主创与剧情简介，快速找到你想看的片子。"
        breadcrumb={[{ label: "作品库" }]}
        eyebrow="Works Library"
      />

      <section className="w-full px-4 md:px-6">
        <div className="rounded-lg border border-background-200 bg-background-50 px-4 md:px-6 py-2 mb-6">
          <FilterGroup label="类型" options={genreOptions} value={genre} onChange={update(setGenre)} />
          <FilterGroup label="年代" options={yearOptions} value={year} onChange={update(setYear)} />
          <FilterGroup label="地区" options={regionOptions} value={region} onChange={update(setRegion)} />
          <FilterGroup label="排序" options={sortOptions} value={sort} onChange={update(setSort)} />
        </div>

        <div className="flex items-center justify-between mb-4">
          <p className="text-sm text-foreground-500">
            共 <span className="text-primary-600 font-medium">{filtered.length}</span> 部作品
          </p>
        </div>

        {pageItems.length > 0 ? (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-5">
            {pageItems.map((work) => (
              <WorkCard key={work.id} work={work} />
            ))}
          </div>
        ) : (
          <EmptyState
            title="无符合条件作品"
            description="当前筛选组合下没有匹配的作品，您可以调整筛选条件后重试。"
            actionLabel="重置筛选条件"
            onAction={reset}
          />
        )}

        {filtered.length > 0 && (
          <Pagination current={currentPage} total={totalPages} onChange={setPage} />
        )}
      </section>
    </div>
  );
}